import { useState } from "react";
import { LifeBuoy, Send, Check } from "lucide-react";
import client from "../lib/api";

const CATEGORIES = [
  { key: "question", label: "A question" },
  { key: "bug", label: "Something's broken" },
  { key: "feedback", label: "Feedback or an idea" },
  { key: "account", label: "Account or access" },
];

/**
 * Support. Send the team a message. It is saved right away so nothing is
 * lost, and we follow up by email. Honest about what happens next.
 */
export default function SupportScreen() {
  const [category, setCategory] = useState("question");
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!message.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await client.post("/help/tickets", {
        category, subject: subject.trim(), email: email.trim(), message: message.trim(),
      });
      setSent(data);
      setSubject(""); setMessage("");
    } catch (_e) {
      setErr("That did not send. Give it another go in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-16 text-center" data-testid="support-sent">
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "hsl(var(--klareet-teal) / 0.15)", color: "hsl(var(--klareet-teal))" }}>
          <Check size={26} />
        </div>
        <h1 className="klareet-serif mb-2" style={{ fontSize: "1.6rem" }}>Got it. Message saved.</h1>
        <p className="k-quiet">We read every one. If you left an email, we'll reply there. Reference <span className="klareet-mono">{String(sent.id).slice(-6)}</span>.</p>
        <button onClick={() => setSent(null)} className="k-btn k-btn-ghost mt-5" data-testid="support-another">Send another</button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 pt-8 pb-24">
      <div className="k-label mb-2 flex items-center gap-2"><LifeBuoy size={13} /> support</div>
      <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="support-title">
        How can we help?
      </h1>
      <p className="k-hero-sub mb-6">Tell us what's up. Your message goes straight to the team.</p>

      <div className="k-card space-y-4" data-testid="support-form">
        <div>
          <label className="k-label mb-1 block">what's this about</label>
          <div className="flex flex-wrap gap-2" data-testid="support-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={`k-pill ${category === c.key ? "k-pill-on" : ""}`}
                data-testid={`support-cat-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="k-label mb-1 block">subject (optional)</label>
          <input className="k-input" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} placeholder="one line" data-testid="support-subject" />
        </div>
        <div>
          <label className="k-label mb-1 block">email to reply to (optional)</label>
          <input className="k-input" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="support-email" />
        </div>
        <div>
          <label className="k-label mb-1 block">your message</label>
          <textarea className="k-input klareet-serif" rows={5} value={message} maxLength={4000} onChange={(e) => setMessage(e.target.value)} placeholder="say it how you'd say it" data-testid="support-message" />
        </div>
        {err && <div className="text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="support-error">{err}</div>}
        <div className="flex justify-end">
          <button onClick={submit} disabled={busy || !message.trim()} className="k-btn k-btn-primary" data-testid="support-submit">
            <Send size={14} /> {busy ? "Sending" : "Send message"}
          </button>
        </div>
      </div>
    </div>
  );
}
